import { Fragment, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { useAlertModal } from '../../components/DialogProvider';
import { UpperInput } from '../../components/inputs/UpperInput';
import { PERFIS, PERFIL_LABEL, type Perfil, formatDataHoraSP } from '@fiodecobre/shared';
import { useAuth } from '../../lib/auth';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { CadastrarRostoModal } from '../equipe/CadastrarRostoModal';
import { MenuAcoesLinha, type AcaoLinha } from '../../components/MenuAcoesLinha';
import { StatusBadge } from '../../components/StatusBadge';
import { SecaoForm } from '../../components/SecaoForm';
import { ComboBox } from '../../components/ComboBox';
import { Interruptor } from '../../components/Interruptor';
import { KeyRound, ShieldCheck, Banknote, Clock } from 'lucide-react';
import { Pencil, ScanFace } from 'lucide-react';

interface Usuario {
  id: number;
  nome: string;
  login: string;
  email: string | null;
  perfil: string;
  ativo: boolean;
  whatsapp_contact_id: number | null;
  departamentos: string[];
  ausente_temporario: boolean;
  controle_acesso_ativo: boolean;
  opera_caixa: boolean;
  // "Ajuda no caixa" — vende sem turno (DA-384).
  vende_sem_turno: boolean;
  // Fornecedor favorecido do titulo de comissao (DA-145).
  fornecedor_id: number | null;
  // Caixa individual por operador (DA-126): nome da gaveta de dinheiro, se ja provisionada.
  caixa_pessoal_nome: string | null;
  criado_em: string;
  // Jornada (DA-456). Colunas `time` do Postgres: chegam como 'HH:MM:SS' ou null.
  hora_inicio: string | null;
  hora_fim: string | null;
  almoco_inicio: string | null;
  almoco_fim: string | null;
  dias_semana: number[] | null;
  sab_hora_inicio: string | null;
  sab_hora_fim: string | null;
  sab_almoco_inicio: string | null;
  sab_almoco_fim: string | null;
}

interface FornecedorOpt { id: number; razao_social: string | null; nome_fantasia: string | null }

// Rotulos dos dias na ordem em que a semana se le (segunda primeiro), com o valor que o
// banco guarda em `dias_semana` (0=domingo..6=sabado, convencao do JS `getDay()`).
const DIAS_SEMANA: Array<{ v: number; label: string }> = [
  { v: 1, label: 'Seg' }, { v: 2, label: 'Ter' }, { v: 3, label: 'Qua' }, { v: 4, label: 'Qui' },
  { v: 5, label: 'Sex' }, { v: 6, label: 'Sáb' }, { v: 0, label: 'Dom' },
];

// 'HH:MM:SS' (Postgres) -> 'HH:MM' (o que o <input type="time"> exibe).
const horaParaInput = (v: string | null | undefined) => (v ? v.slice(0, 5) : '');
// '' -> null: campo de hora vazio significa "sem horario", nao string vazia (o zod recusaria).
const horaParaApi = (v: string) => (v.trim() ? v.trim() : null);

interface FormState {
  nome: string;
  email: string;
  login: string;
  senha: string;
  perfil: Perfil;
  ativo: boolean;
  departamentos: string[];
  ausente_temporario: boolean;
  controle_acesso_ativo: boolean;
  opera_caixa: boolean;
  // DA-384: "Ajuda no caixa" — vende sem turno (dinheiro no Movimento de Caixa).
  vende_sem_turno: boolean;
  // DA-145: fornecedor favorecido do titulo de comissao (vazio = nao gera titulo).
  fornecedor_id: number | null;
  // DA-126: nome da gaveta de dinheiro ja provisionada (so leitura, pra subtexto do toggle).
  caixa_pessoal_nome: string | null;
  // DA-456: jornada. Sempre 'HH:MM' ou '' no form — a conversao pra/da API e nas bordas.
  hora_inicio: string;
  hora_fim: string;
  almoco_inicio: string;
  almoco_fim: string;
  dias_semana: number[];
  sab_hora_inicio: string;
  sab_hora_fim: string;
  sab_almoco_inicio: string;
  sab_almoco_fim: string;
}

const formVazio: FormState = {
  nome: '', email: '', login: '', senha: '', perfil: 'vendedor',
  ativo: true, departamentos: [], ausente_temporario: false, controle_acesso_ativo: true,
  opera_caixa: false, vende_sem_turno: false, fornecedor_id: null, caixa_pessoal_nome: null,
  // Mesmo default da coluna no banco (seg-sex) e horario em branco: usuario novo nasce sem
  // jornada, que e o que o backend ja fazia. Sem horario o sistema nao cobra atraso nem
  // fecha o dia sozinho — quem define e quem cadastra.
  hora_inicio: '', hora_fim: '', almoco_inicio: '', almoco_fim: '',
  dias_semana: [1, 2, 3, 4, 5],
  sab_hora_inicio: '', sab_hora_fim: '', sab_almoco_inicio: '', sab_almoco_fim: '',
};

export function Usuarios() {
  const { usuario: logado } = useAuth();
  const podeEditar = logado?.perfil === 'admin' || logado?.perfil === 'gerente';
  const toast = useToast();
  const { alerta } = useAlertModal();
  const [itens, setItens] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState<Usuario | null>(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState<FormState>(formVazio);
  const [rostosCount, setRostosCount] = useState<Record<number, number>>({});
  const [cadastrarRosto, setCadastrarRosto] = useState<Usuario | null>(null);
  const [fornecedores, setFornecedores] = useState<FornecedorOpt[]>([]);
  const [mostrarInativos, setMostrarInativos] = useState(false);

  async function carregar() {
    setCarregando(true);
    try {
      const [lista, counts] = await Promise.all([
        api.get<Usuario[]>('/api/usuarios'),
        podeEditar ? api.get<Record<number, number>>('/api/funcionarios/rostos-counts').catch(() => ({})) : Promise.resolve({}),
      ]);
      setItens(lista);
      setRostosCount(counts);
    } catch (e: any) {
      toast.erro(e.message);
    } finally { setCarregando(false); }
  }
  useEffect(() => { void carregar(); }, []);

  // Recarga so da contagem de rostos (DA-456): chamada ao fechar o cadastro de rosto, que agora
  // abre de dentro do modal de edicao. Recarregar a LISTA inteira aqui nao serve — o `editando`
  // apontaria pra uma linha antiga e o form seguiria com o que o operador ja digitou e nao salvou.
  async function recarregarRostos() {
    if (!podeEditar) return;
    try {
      setRostosCount(await api.get<Record<number, number>>('/api/funcionarios/rostos-counts'));
    } catch { /* contagem e informativa; falhar aqui nao atrapalha o cadastro */ }
  }

  // Fornecedores pro vinculo do titulo de comissao (DA-145). So admin/gerente edita cadastro.
  useEffect(() => {
    if (!podeEditar) return;
    api.get<FornecedorOpt[]>('/api/fornecedores', { query: { todos: true } }).then(setFornecedores).catch(() => setFornecedores([]));
  }, [podeEditar]);

  const nomeFornecedor = (f: FornecedorOpt) => f.razao_social || f.nome_fantasia || `#${f.id}`;

  // Quantas capturas de rosto o usuario em edicao ja tem (DA-456). 5 e o cadastro completo.
  const rostosFeitos = editando ? (rostosCount[editando.id] ?? 0) : 0;

  const filtrados = itens.filter((u) =>
    (mostrarInativos || u.ativo) &&
    (u.nome.toLowerCase().includes(busca.toLowerCase()) || u.login.toLowerCase().includes(busca.toLowerCase())),
  );

  function abrirNovo() {
    setEditando(null);
    setForm(formVazio);
    setModal(true);
  }
  function abrirEditar(u: Usuario) {
    setEditando(u);
    setForm({
      nome: u.nome, email: u.email ?? '', login: u.login, senha: '',
      perfil: u.perfil as Perfil, ativo: u.ativo,
      departamentos: u.departamentos, ausente_temporario: u.ausente_temporario,
      controle_acesso_ativo: u.controle_acesso_ativo,
      opera_caixa: u.opera_caixa,
      vende_sem_turno: u.vende_sem_turno ?? false,
      fornecedor_id: u.fornecedor_id ?? null,
      caixa_pessoal_nome: u.caixa_pessoal_nome ?? null,
      hora_inicio: horaParaInput(u.hora_inicio),
      hora_fim: horaParaInput(u.hora_fim),
      almoco_inicio: horaParaInput(u.almoco_inicio),
      almoco_fim: horaParaInput(u.almoco_fim),
      dias_semana: u.dias_semana ?? [1, 2, 3, 4, 5],
      sab_hora_inicio: horaParaInput(u.sab_hora_inicio),
      sab_hora_fim: horaParaInput(u.sab_hora_fim),
      sab_almoco_inicio: horaParaInput(u.sab_almoco_inicio),
      sab_almoco_fim: horaParaInput(u.sab_almoco_fim),
    });
    setModal(true);
  }

  function toggleDepartamento(dep: string) {
    setForm((f) => ({
      ...f,
      departamentos: f.departamentos.includes(dep)
        ? f.departamentos.filter((d) => d !== dep)
        : [...f.departamentos, dep],
    }));
  }

  function toggleDia(v: number) {
    setForm((f) => ({
      ...f,
      dias_semana: f.dias_semana.includes(v)
        ? f.dias_semana.filter((d) => d !== v)
        : [...f.dias_semana, v].sort((a, b) => a - b),
    }));
  }

  async function salvar() {
    try {
      const payload: Record<string, unknown> = {
        nome: form.nome, email: form.email || null, login: form.login,
        perfil: form.perfil, ativo: form.ativo,
        departamentos: form.departamentos,
        ausente_temporario: form.ausente_temporario,
        controle_acesso_ativo: form.controle_acesso_ativo,
        opera_caixa: form.opera_caixa,
        vende_sem_turno: form.vende_sem_turno,
        fornecedor_id: form.fornecedor_id,
        // Jornada (DA-456). Campo vazio vai como null, nao como '' — e o que apaga o horario.
        hora_inicio: horaParaApi(form.hora_inicio),
        hora_fim: horaParaApi(form.hora_fim),
        almoco_inicio: horaParaApi(form.almoco_inicio),
        almoco_fim: horaParaApi(form.almoco_fim),
        dias_semana: form.dias_semana,
        sab_hora_inicio: horaParaApi(form.sab_hora_inicio),
        sab_hora_fim: horaParaApi(form.sab_hora_fim),
        sab_almoco_inicio: horaParaApi(form.sab_almoco_inicio),
        sab_almoco_fim: horaParaApi(form.sab_almoco_fim),
      };
      if (form.senha) payload.senha = form.senha;
      if (editando) {
        // `gaveta_aviso` (DA-398): o cadastro FOI salvo, mas a gaveta continua ativa porque tem
        // dinheiro ou turno aberto. Vai em modal bloqueante, nao toast — e uma pendencia de
        // dinheiro que o operador precisa ler, e toast some sozinho.
        const r = await api.put<{ gaveta_aviso?: string }>(`/api/usuarios/${editando.id}`, payload);
        toast.sucesso('atualizado');
        if (r?.gaveta_aviso) await alerta(r.gaveta_aviso, { titulo: 'Gaveta do caixa continua ativa' });
      } else {
        if (!form.senha) { toast.erro('senha obrigatoria'); return; }
        await api.post('/api/usuarios', payload);
        toast.sucesso('criado');
      }
      setModal(false);
      void carregar();
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  const sticky = useStickyHeader();

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">Usuarios</h2>
          <div className="pagina-acoes">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
              Mostrar inativos
            </label>
            <input type="search" placeholder="Buscar..." className="busca" value={busca} onChange={(e) => setBusca(e.target.value)} />
            {podeEditar && <button type="button" className="btn-primario" onClick={abrirNovo}>+ Novo</button>}
          </div>
        </div>
      </div>

      {carregando ? <p className="muted">Carregando...</p> : filtrados.length === 0 ? <p className="muted">Nenhum usuario.</p> : (
        <table className="tabela" style={sticky.tableStyle}>
          <thead><tr>
            <th style={sticky.thStyle}>Nome</th>
            <th style={sticky.thStyle}>Login</th>
            <th style={sticky.thStyle}>Perfil</th>
            <th style={sticky.thStyle}>Status</th>
            <th style={{ ...sticky.thStyle, textAlign: 'right' }}>Acoes</th>
          </tr></thead>
          <tbody>
            {filtrados.map((u) => {
              // DA-456: o menu da linha ficou SO com Editar. "Cadastrar rosto" foi pra dentro do
              // modal (coluna Ponto e horario, junto do resto do ponto) e "Inativar" era o mesmo
              // que desligar o interruptor Ativo la dentro — duas portas pro mesmo efeito.
              const acoes: AcaoLinha[] = [
                { label: 'Editar', icon: Pencil, oculto: !podeEditar, onClick: () => abrirEditar(u) },
              ];
              return (
                <tr key={u.id} onDoubleClick={() => podeEditar && abrirEditar(u)}>
                  <td>{u.nome}</td>
                  <td>{u.login}</td>
                  <td>{PERFIL_LABEL[u.perfil as Perfil] ?? u.perfil}</td>
                  <td><StatusBadge ativo={u.ativo} /></td>
                  <td style={{ textAlign: 'right' }}>
                    <MenuAcoesLinha acoes={acoes} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* TRES COLUNAS EM TELA CHEIA (DA-456; era duas em caixa de 980, DA-397). A 3a coluna
          "Ponto e horario" nasceu com a jornada — os 9 campos de horario existiam no banco desde
          a Onda 1 e nenhuma tela os gravava. Em caixa isso nao cabia mais no tablet em pe, e o
          Ruan escolheu tela cheia em todos os aparelhos. Grid auto-responsivo: 3 colunas no
          desktop, 2 no tablet deitado, 1 no tablet em pe — sem media query, o `auto-fit` decide. */}
      <Modal aberto={modal} onFechar={() => setModal(false)} titulo={editando ? 'Editar usuario' : 'Novo usuario'} telaCheia
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={() => setModal(false)}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={salvar} disabled={!form.nome || !form.login}>Salvar</button>
          </>
        }
      >
        {/* minmax(430px): NAO e chute. A tabela de horario mede 438px (rotulo + 4 campos de hora
            + o traco do almoco), e campo `type=time` abaixo de ~85px corta o "--:--" e o icone do
            relogio. Com 330px a coluna dava 335px no tablet em pe e a tabela transbordava calada —
            medido em prod, nao se deduz lendo o CSS. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(430px, 1fr))', gap: 30, alignItems: 'start', width: '100%', maxWidth: 1700, margin: '0 auto' }}>
        <div className="form-grid">
          <SecaoForm icone={KeyRound} titulo="Acesso" primeira />
          <label className="full">
            Nome
            <UpperInput value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} autoFocus />
          </label>
          <label>
            Login
            {/* Destravado na edicao em 2026-08-20 a pedido do Ruan (era `disabled={!!editando}` desde a
                Onda 2). O `PUT` sempre aceitou o campo — ninguem mandava porque o input estava morto.
                As duas travas que o front NAO tem como conhecer (login duplicado e login cravado no
                codigo) moram no backend, em `usuarios.routes.ts`. */}
            <UpperInput value={form.login} onChange={(v) => setForm({ ...form, login: v })} />
          </label>
          <label>
            Email
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <label>
            Perfil
            <select value={form.perfil} onChange={(e) => setForm({ ...form, perfil: e.target.value as Perfil })}>
              {PERFIS.map((p) => <option key={p} value={p}>{PERFIL_LABEL[p]}</option>)}
            </select>
          </label>
          <label>
            {editando ? 'Nova senha (deixe vazio pra manter)' : 'Senha'}
            <input type="password" value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} />
          </label>
          {/* Data do cadastro (pedido do Ruan, 02/10/2026): e' o `criado_em`, que o banco carimba
              sozinho no INSERT — nao ha o que digitar. "Editbox travado" (molde no ModalFinalizarVenda):
              moldura de input com a fonte em `--input-fg`, porque a regra !important dos inputs nao
              alcanca um <div>. No usuario novo diz que a data nasce ao salvar, em vez de ficar vazio. */}
          <label>
            Cadastrado em
            <div
              style={{
                padding: '8px 14px', borderRadius: 4, cursor: 'not-allowed',
                background: 'var(--input-bg)', border: '1px solid var(--input-border, #e5e7eb)',
                color: editando ? 'var(--input-fg)' : 'var(--text-muted)',
              }}
              title="Preenchida pelo sistema no momento do cadastro"
            >
              {editando ? formatDataHoraSP(editando.criado_em) : 'Preenchida ao salvar'}
            </div>
          </label>
          {/* Comissao ganhou secao PROPRIA: pendurada no fim de "Permissoes e situacao" ela lia como
              permissao, e nao e — e onde o titulo de Contas a Pagar vai cair. */}
          <SecaoForm icone={Banknote} titulo="Comissão" />
          <label className="full" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
            Fornecedor (favorecido da comissao)
            <ComboBox
              opcoes={[
                { value: '', label: '— Nenhum (nao gera titulo de comissao)' },
                ...fornecedores.map((f) => ({ value: f.id, label: nomeFornecedor(f) })),
              ]}
              value={form.fornecedor_id ?? ''}
              onChange={(v) => setForm({ ...form, fornecedor_id: v ? Number(v) : null })}
              placeholder="— Nenhum (nao gera titulo de comissao)"
            />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Aponta o titulo de comissao em Contas a Pagar pra este fornecedor. A vendedora precisa estar cadastrada como Fornecedor.
            </span>
          </label>
        </div>

        <div className="form-grid">
          <SecaoForm icone={ShieldCheck} titulo="Permissões e situação" primeira />
          {/* PRIMEIRO campo da coluna (DA-398): e aqui que se decide se a pessoa entra no sistema —
              era um checkbox no meio de outros dois, com o mesmo peso visual de "Compras".
              Desligar leva o CONTROLE DE PONTO junto (ninguem que saiu da loja bate ponto) e tira a
              gaveta do caixa do ar; o backend refaz as duas coisas por conta propria, o front so
              deixa isso VISIVEL na hora em vez de virar surpresa depois de salvar. */}
          <div className="full">
            <Interruptor
              ligado={form.ativo}
              onChange={(lig) => setForm({ ...form, ativo: lig, controle_acesso_ativo: lig })}
              rotuloLigado="Ativo"
              rotuloDesligado="Inativo"
              ajuda={form.ativo
                ? 'Entra no sistema normalmente.'
                : 'Não faz login, sai da distribuição de conversas, não bate ponto e a gaveta do caixa é desativada. O histórico dele é preservado.'}
            />
          </div>
          <label className="full" style={{ flexDirection: 'row', gap: 16, alignItems: 'center' }}>
            <span>Departamentos:</span>
            <label style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={form.departamentos.includes('atendimento')} onChange={() => toggleDepartamento('atendimento')} /> Atendimento
            </label>
            <label style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={form.departamentos.includes('compras')} onChange={() => toggleDepartamento('compras')} /> Compras
            </label>
          </label>
          {/* DA-384: caixa em 3 modos mutuamente exclusivos (radio). O "Opera o caixa" (gaveta
              pessoal + turno, DA-126/DA-343) ganhou um irmao mais leve — "Ajuda no caixa": finaliza
              venda quando o movimento aperta, mas sem gaveta propria nem turno (o dinheiro dela cai
              no Movimento de Caixa). O subtexto de cada modo diz o que decide o fechamento, porque
              regra de comportamento tem que estar escrita onde se marca. */}
          <label className="full" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>Caixa</span>
            {([
              { v: 'nenhum', t: 'Não opera o caixa', d: 'Não finaliza venda no caixa.' },
              { v: 'ajuda', t: 'Ajuda no caixa (vende sem turno)', d: 'Finaliza venda quando o movimento aperta — emite nota, recebe cartão/pix/débito/crédito. O dinheiro dela cai no Movimento de Caixa; sem gaveta própria nem contagem no fim do dia.' },
              { v: 'completo', t: 'Opera o caixa (gaveta própria)', d: form.caixa_pessoal_nome
                  ? `Abre/fecha turno; o dinheiro vai pra ${form.caixa_pessoal_nome}, que fecha sempre contando as cédulas.`
                  : 'Abre/fecha turno; uma gaveta de dinheiro própria é criada automaticamente e fecha sempre contando as cédulas.' },
            ] as const).map((op) => {
              const modo = form.opera_caixa ? 'completo' : form.vende_sem_turno ? 'ajuda' : 'nenhum';
              return (
                <label key={op.v} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 6, cursor: 'pointer' }}>
                  <input type="radio" name="modo_caixa" checked={modo === op.v} style={{ marginTop: 3 }}
                    onChange={() => setForm({ ...form, opera_caixa: op.v === 'completo', vende_sem_turno: op.v === 'ajuda' })} />
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>{op.t}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{op.d}</span>
                  </span>
                </label>
              );
            })}
          </label>
          <label style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.ausente_temporario} onChange={(e) => setForm({ ...form, ausente_temporario: e.target.checked })} /> Ausente temporario
          </label>
        </div>

        {/* ───────── COLUNA 3: PONTO E HORARIO (DA-456) ─────────
            Junta o que era do mesmo assunto e vivia espalhado: o cadastro de rosto (escondido no
            menu ⋮ da linha), o "Controle de ponto ativo" (perdido no meio de Permissoes, entre
            "Compras" e "Caixa") e a jornada — que ate aqui nao tinha tela nenhuma. */}
        <div className="form-grid">
          <SecaoForm icone={Clock} titulo="Ponto e horário" primeira />

          {/* Cartao do FR: mostra o ESTADO, nao so a acao. Quantas capturas ele tem e se ja da
              pra bater ponto — antes so dava pra saber lendo o "(FR N)" do menu da linha. */}
          {editando ? (
            <div className="full" style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px',
              borderRadius: 10, border: '1px solid var(--border)', background: 'var(--page-bg)',
            }}>
              <div style={{
                flex: '0 0 auto', width: 38, height: 38, borderRadius: 10, display: 'grid', placeItems: 'center',
                background: rostosFeitos >= 5 ? 'var(--positive-bg, #fde68a)' : '#f97316',
                color: rostosFeitos >= 5 ? 'var(--positive-fg, #000)' : '#000',
              }}>
                <ScanFace size={21} />
              </div>
              <div style={{ flex: '1 1 auto', minWidth: 0, lineHeight: 1.35 }}>
                <b style={{ display: 'block', fontSize: 13.5 }}>Reconhecimento facial</b>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {rostosFeitos >= 5
                    ? `${rostosFeitos} capturas — pronto pra bater ponto.`
                    : rostosFeitos > 0
                      ? `Só ${rostosFeitos} de 5 capturas — ainda não reconhece bem.`
                      : 'Sem rosto cadastrado — não consegue bater ponto.'}
                </span>
              </div>
              <button type="button" className="btn-secundario" style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}
                onClick={() => setCadastrarRosto(editando)}>
                {rostosFeitos > 0 ? 'Refazer' : 'Cadastrar'}
              </button>
            </div>
          ) : (
            <span className="full" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              O rosto pro ponto é cadastrado depois de salvar, reabrindo este usuário.
            </span>
          )}

          {/* Some quando inativo: nao ha o que decidir sobre o ponto de quem nao trabalha mais
              aqui (e o valor ja foi zerado pelo interruptor Ativo). Volta ao religar. */}
          {form.ativo && (
            <>
              <label className="full" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={form.controle_acesso_ativo}
                  onChange={(e) => setForm({ ...form, controle_acesso_ativo: e.target.checked })} />
                <b>Controle de ponto ativo</b>
              </label>
              <span className="full" style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -6 }}>
                Desligado, ele entra e sai sem bater ponto — e o horário abaixo deixa de valer.
              </span>

              <div className="full" style={{ opacity: form.controle_acesso_ativo ? 1 : 0.45 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 7 }}>Dias de trabalho</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {DIAS_SEMANA.map((d) => {
                    const marcado = form.dias_semana.includes(d.v);
                    return (
                      <button key={d.v} type="button" disabled={!form.controle_acesso_ativo}
                        aria-pressed={marcado} onClick={() => toggleDia(d.v)}
                        style={{
                          minWidth: 46, padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 600,
                          cursor: form.controle_acesso_ativo ? 'pointer' : 'not-allowed',
                          border: `1px solid ${marcado ? 'var(--primary)' : 'var(--input-border)'}`,
                          background: marcado ? 'var(--primary)' : 'var(--input-bg)',
                          color: marcado ? '#fff' : 'var(--text-muted)',
                        }}>
                        {d.label}
                      </button>
                    );
                  })}
                </div>

                {/* Duas linhas e nao sete de proposito: o banco guarda UM horario pra semana e
                    outro pro sabado. Sete linhas prometeriam terca diferente de quarta. */}
                {/* overflow-x auto e nao hidden: em celular estreito a tabela ainda passa da
                    coluna, e cortar deixaria o campo do almoco INALCANCAVEL (sem barra pra rolar
                    ate ele). Rola dentro do proprio bloco, sem sacudir a pagina. */}
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 438, borderCollapse: 'separate', borderSpacing: '0 6px', marginTop: 12 }}>
                  <thead>
                    <tr>
                      <th />
                      <th style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-muted)', padding: '0 4px 2px' }}>Entrada</th>
                      <th style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-muted)', padding: '0 4px 2px' }}>Saída</th>
                      <th colSpan={3} style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-muted)', padding: '0 4px 2px' }}>Almoço</th>
                    </tr>
                  </thead>
                  <tbody>
                    {([
                      { rot: 'Seg a sex', ativa: form.dias_semana.some((d) => d >= 1 && d <= 5),
                        campos: ['hora_inicio', 'hora_fim', 'almoco_inicio', 'almoco_fim'] as const },
                      { rot: 'Sábado', ativa: form.dias_semana.includes(6),
                        campos: ['sab_hora_inicio', 'sab_hora_fim', 'sab_almoco_inicio', 'sab_almoco_fim'] as const },
                    ]).map((linha) => {
                      const lig = form.controle_acesso_ativo && linha.ativa;
                      // Campo travado diz por que: sem isto a linha do sabado parece quebrada
                      // (o dia desmarcado fica varios centimetros acima, em outro bloco).
                      const avisoDia = form.controle_acesso_ativo && !linha.ativa
                        ? (linha.rot === 'Sábado'
                          ? 'Marque Sáb em "Dias de trabalho" acima pra preencher o horário de sábado.'
                          : 'Marque ao menos um dia de Seg a Sex acima pra preencher este horário.')
                        : null;
                      return (
                        <Fragment key={linha.rot}>
                        <tr>
                          <td style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', paddingRight: 8, color: lig ? undefined : 'var(--text-muted)' }}>{linha.rot}</td>
                          {linha.campos.slice(0, 3).map((campo) => (
                            <td key={campo} style={{ padding: '0 4px' }}>
                              <input type="time" disabled={!lig} value={form[campo]}
                                onChange={(e) => setForm({ ...form, [campo]: e.target.value })}
                                style={{ width: '100%', textAlign: 'center', padding: '8px 6px' }} />
                            </td>
                          ))}
                          <td style={{ textAlign: 'center', color: 'var(--text-muted)', width: 14 }}>–</td>
                          <td style={{ padding: '0 4px' }}>
                            <input type="time" disabled={!lig} value={form[linha.campos[3]]}
                              onChange={(e) => setForm({ ...form, [linha.campos[3]]: e.target.value })}
                              style={{ width: '100%', textAlign: 'center', padding: '8px 6px' }} />
                          </td>
                        </tr>
                        {avisoDia && (
                          <tr>
                            <td />
                            <td colSpan={5} style={{ fontSize: 12, color: 'var(--text-muted)', padding: '0 4px' }}>
                              {avisoDia}
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
                </div>

                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.45 }}>
                  A <b>entrada</b> é o que decide o atraso (com a tolerância de <i>Equipe → Config. do ponto</i>).
                  A <b>saída</b> é o que o sistema usa pra fechar o dia sozinho às 23h se ele esquecer de bater.
                  Sem horário, nenhum dos dois vale.
                </span>
                <span style={{ display: 'block', fontSize: 12, marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--positive-bg, #fde68a)', color: 'var(--positive-fg, #000)', lineHeight: 1.45 }}>
                  <b>Atenção:</b> o <b>almoço</b> hoje é só informativo — o sistema grava e mostra aqui,
                  mas ainda não cobra batida no intervalo nem desconta as horas da jornada.
                </span>
              </div>
            </>
          )}
        </div>
        </div>
      </Modal>

      {/* Abre POR CIMA do modal de editar (z-[1100] la dentro) e, ao fechar, recarrega so a
          contagem de rostos — o modal de edicao continua aberto com o que ja foi digitado. */}
      {cadastrarRosto && (
        <CadastrarRostoModal
          usuario={{ id: cadastrarRosto.id, nome: cadastrarRosto.nome }}
          onFechar={() => { setCadastrarRosto(null); void recarregarRostos(); }}
        />
      )}
    </div>
  );
}
